"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

interface ClientData {
    id: string;
    name: string;
    role: string;
    projectTitle: string;
    testimonial: string;
    clientImage: string;
    projectImages: {
        desktop: string;
        mobile: string;
    };
    blogUrl: string;
    altText: string;
}

const cardsData: ClientData[] = [
    {
        id: "00",
        name: "Harsh Lunavat",
        role: "Cinematographer",
        projectTitle: "Portfolio for Cinematographer",
        testimonial: "\"The portfolio perfectly captures my cinematic style. It's minimal yet powerful.\"",
        clientImage: "/clients/hl.avif",
        projectImages: {
            desktop: "/websites/hl1.avif",
            mobile: "/websites/hl.avif",
        },
        blogUrl: "/blog/portfolio-that-updates-itself",
        altText: "Automated Video Portfolio Infrastructure using Google Drive API and Mux",
    },
    {
        id: "01",
        name: "Srishti Parkhi",
        role: "Interior Designer",
        projectTitle: "Portfolio for Interior Designer",
        testimonial: "\"A beautifully designed showcase for my interior projects. Exactly what I needed.\"",
        clientImage: "/clients/is.avif",
        projectImages: {
            desktop: "/websites/is1.avif",
            mobile: "/websites/is.avif",
        },
        blogUrl: "/blog/design-that-reassures",
        altText: "Minimalist Interior Design Portfolio with smooth animations",
    },
    {
        id: "02",
        name: "Aayush More",
        role: "Finance Tutor",
        projectTitle: "LMS Platform for Finance Tutor",
        testimonial: "\"The LMS platform has transformed how I teach finance. Seamless and efficient.\"",
        clientImage: "/clients/mwc.avif",
        projectImages: {
            desktop: "/websites/mwc1.avif",
            mobile: "/websites/mwc.avif",
        },
        blogUrl: "/blog/lms-with-zero-bills",
        altText: "Zero-cost Learning Management System (LMS) Platform",
    },
    {
        id: "03",
        name: "Planet Fitness",
        role: "Gym Franchise",
        projectTitle: "Brochure Website for Gym",
        testimonial: "\"They delivered a complete, professional website in 48 hours. No subscriptions, just a straightforward site that works perfectly.\"",
        clientImage: "/clients/pf.avif",
        projectImages: {
            desktop: "/websites/pf1.avif",
            mobile: "/websites/pf.avif",
        },
        blogUrl: "/blog/two-days-from-call-to-launch",
        altText: "Fast-loading Static Site for Planet Fitness Gym Franchise",
    },
];

export default function ClientsSection() {
    // --- CAROUSEL LOGIC ---
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(false);

    // --- SWIPE LOGIC ---
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [swipeOffset, setSwipeOffset] = useState(0);

    // --- HOVER LOGIC ---
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const nextSlide = () => {
        const maxIndex = isMobile ? cardsData.length - 1 : cardsData.length - 3;
        if (currentIndex < maxIndex) {
            setCurrentIndex((prev) => prev + 1);
        }
    };

    const prevSlide = () => {
        if (currentIndex > 0) {
            setCurrentIndex((prev) => prev - 1);
        }
    };

    const onTouchStart = (e: React.TouchEvent) => {
        if (!isMobile) return;
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e: React.TouchEvent) => {
        if (!isMobile || touchStart === null) return;
        const currentTouch = e.targetTouches[0].clientX;
        setSwipeOffset(currentTouch - touchStart);
    };

    const onTouchEnd = () => {
        if (!isMobile || touchStart === null) return;

        const minSwipeDistance = 50;
        if (swipeOffset > minSwipeDistance) {
            prevSlide();
        } else if (swipeOffset < -minSwipeDistance) {
            nextSlide();
        }

        setTouchStart(null);
        setSwipeOffset(0);
    };

    // --- MODAL LOGIC ---
    const [modalOpen, setModalOpen] = useState(false);
    const [modalData, setModalData] = useState<ClientData | null>(null);
    const [isClosing, setIsClosing] = useState(false);

    const openModal = (data: ClientData) => {
        setModalData(data);
        setModalOpen(true);
        setIsClosing(false);
    };

    const closeModal = () => {
        setIsClosing(true);
        setTimeout(() => {
            setModalOpen(false);
            setIsClosing(false);
            setModalData(null);
        }, 300);
    };

    // --- RENDER HELPERS ---
    const getCardStyle = (index: number) => {
        const pos = index - currentIndex;
        // Base gap for desktop (approx 5px visual gap)
        const gap = "10px"; 
        
        const style: React.CSSProperties = {
            opacity: 1,
            zIndex: 1,
            transform: "translateY(-50%)",
            pointerEvents: "none" as const,
            position: "absolute" as const,
            top: "50%",
            transition: "all 0.3s cubic-bezier(0.25, 1, 0.5, 1)", // Speed up slightly for snappier pop
            height: "400px",
            backgroundColor: "#e4e2dd",
            border: "3px solid #1a1a1a",
            display: "flex",
            flexDirection: "column" as const,
            cursor: "default",
        };

        if (isMobile) {
            style.width = "80%";
            if (pos === 0) {
                style.left = "50%";
                style.transform = "translateY(-50%) translateX(-50%)";
                style.zIndex = 2;
                style.pointerEvents = "auto";
                style.boxShadow = "4px 4px 0px #1a1a1a";
            } else if (pos < 0) {
                style.left = "-50%";
                style.transform = "translateY(-50%) translateX(-50%)";
                style.zIndex = 0;
            } else {
                style.left = "150%";
                style.transform = "translateY(-50%) translateX(-50%)";
                style.zIndex = 0;
            }
        } else {
            // Desktop Logic: Show 3 cards with gaps
            // We'll use calc to create precise gaps
            const cardWidth = "30%"; 
            
            if (pos < 0) {
                // Left offscreen - Shrunk
                style.left = "0%";
                style.width = cardWidth;
                style.transform =
                    "translateY(-50%) scale(0.8) translateX(-50%)";
                style.zIndex = 0;
            } else if (pos === 0) {
                // Left visible
                style.left = "5%";
                style.width = `calc(${cardWidth} - ${gap})`;
                style.pointerEvents = "auto";
                if (index === hoveredIndex) {
                     style.transform = "translateY(calc(-50% - 6px)) translateX(-6px)";
                     style.boxShadow = "10px 10px 0px #1a1a1a";
                } else {
                     style.boxShadow = "4px 4px 0px #1a1a1a";
                }
            } else if (pos === 1) {
                // Center visible
                style.left = `calc(35% + ${gap})`; 
                style.width = `calc(${cardWidth} - ${gap})`;
                style.zIndex = 2;
                style.pointerEvents = "auto";
                if (index === hoveredIndex) {
                     style.transform = "translateY(calc(-50% - 6px)) translateX(-6px)";
                     style.boxShadow = "10px 10px 0px #1a1a1a";
                } else {
                     style.boxShadow = "4px 4px 0px #1a1a1a";
                }
            } else if (pos === 2) {
                // Right visible
                style.left = `calc(65% + ${gap} * 2)`;
                style.width = `calc(${cardWidth} - ${gap})`;
                style.pointerEvents = "auto";
                if (index === hoveredIndex) {
                     style.transform = "translateY(calc(-50% - 6px)) translateX(-6px)";
                     style.boxShadow = "10px 10px 0px #1a1a1a";
                } else {
                     style.boxShadow = "4px 4px 0px #1a1a1a";
                }
            } else {
                // Right offscreen - Shrunk
                style.left = "100%";
                style.width = cardWidth;
                style.transform =
                    "translateY(-50%) scale(0.8) translateX(-50%)";
                style.zIndex = -pos;
            }
        }

        return style;
    };

    return (
        <section className="clients-section py-16 px-8 border-b-[3px] border-[#1a1a1a] overflow-hidden relative">
            <div className="section-label mb-8 bg-white border-[2px] border-[#1a1a1a] px-2 py-1 inline-block text-sm">
                OUR CLIENTS
            </div>

            <div className="carousel-container relative w-full max-w-[1200px] mx-auto h-[420px] flex items-center justify-center">
                <div
                    className="carousel-track relative w-full h-full"
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    style={{
                        transform: `translateX(${swipeOffset}px)`,
                        transition:
                            touchStart !== null
                                ? "none"
                                : "transform 0.3s ease-out",
                    }}
                >
                    {cardsData.map((client, index) => (
                        <div
                            key={client.id}
                            style={getCardStyle(index)}
                            className="client-card"
                            onMouseEnter={() => setHoveredIndex(index)}
                            onMouseLeave={() => setHoveredIndex(null)}
                        >
                            {/* Card Top - Testimonial Display */}
                            <div className="card-top flex-1 border-b-[3px] border-[#1a1a1a] flex items-center justify-center p-6 text-center bg-white">
                                <p className="text-lg md:text-xl italic font-serif leading-relaxed">
                                    {client.testimonial}
                                </p>
                            </div>

                            {/* Card Bottom - Client Info */}
                            <div className="card-bottom p-4 flex items-center gap-4 h-[120px]">
                                <div className="client-img w-[60px] h-[60px] bg-[#ccc] rounded-[10px] border-[2px] border-[#1a1a1a] overflow-hidden relative">
                                    <Image src={client.clientImage} alt={`Portrait of ${client.name}, ${client.role}`} fill className="object-cover" />
                                </div>
                                <div className="client-info flex-1">
                                    <div className="client-name font-bold uppercase">
                                        {client.name}
                                    </div>
                                    <div className="client-role text-[0.8rem] opacity-70">
                                        {client.role}
                                    </div>
                                </div>
                                <button
                                    className="card-btn w-[40px] h-[40px] bg-white border-[2px] border-[#1a1a1a] flex items-center justify-center hover:bg-[#1a1a1a] hover:text-[#e4e2dd] transition-all z-[2] pixel-font text-[1.5rem] cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openModal(client);
                                    }}
                                >
                                    →
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div className="flex justify-center mt-2 md:hidden opacity-60 text-sm">
                <span>Swipe to view more</span>
            </div>
            <div className="carousel-footer grid grid-cols-[1fr_auto_1fr] items-center max-w-[1200px] mx-auto mt-2 px-8">
                <div className="footer-spacer"></div>
                <Link
                    href="/work"
                    className="underline hover:text-[#1a1a1a]/70"
                >
                    View All Work
                </Link>
                <div
                    className={`carousel-controls flex gap-4 justify-self-end ${
                        isMobile ? "hidden" : ""
                    }`}
                >
                    <div
                        className="nav-btn w-[50px] h-[50px] bg-[#e4e2dd] border-[3px] border-[#1a1a1a] shadow-[4px_4px_0px_#1a1a1a] flex items-center justify-center cursor-pointer text-[1.5rem] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none select-none hover:bg-white transition-all"
                        onClick={prevSlide}
                    >
                        &lt;
                    </div>
                    <div
                        className="nav-btn w-[50px] h-[50px] bg-[#e4e2dd] border-[3px] border-[#1a1a1a] shadow-[4px_4px_0px_#1a1a1a] flex items-center justify-center cursor-pointer text-[1.5rem] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none select-none hover:bg-white transition-all"
                        onClick={nextSlide}
                    >
                        &gt;
                    </div>
                </div>
            </div>

            {/* MODAL */}
            {modalOpen && modalData && (
                <div
                    className={`fixed top-0 left-0 w-full h-full bg-black/80 z-[9999] flex items-center justify-center p-4 transition-opacity duration-300 ${
                        isClosing ? "opacity-0" : "opacity-100"
                    }`}
                    onClick={closeModal}
                >
                    <div
                        className={`bg-[#e4e2dd] border-[3px] border-[#1a1a1a] shadow-[8px_8px_0px_#1a1a1a] w-full max-w-[1000px] max-h-[90vh] overflow-y-auto flex flex-col transition-transform duration-300 ${
                            isClosing
                                ? "scale-95 opacity-0"
                                : "scale-100 opacity-100"
                        }`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="bg-[#1a1a1a] text-[#e4e2dd] p-2 px-4 flex justify-between items-center font-mono select-none">
                            <span>project_view.exe</span>
                            <div className="flex gap-2">
                                <div
                                    className="w-4 h-4 bg-[#e4e2dd] text-[#1a1a1a] flex items-center justify-center cursor-pointer hover:bg-white text-xs font-bold"
                                    onClick={closeModal}
                                >
                                    -
                                </div>
                                <div
                                    className="w-4 h-4 bg-[#e4e2dd] text-[#1a1a1a] flex items-center justify-center cursor-pointer hover:bg-white text-xs font-bold"
                                    onClick={closeModal}
                                >
                                    x
                                </div>
                            </div>
                        </div>
                        <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Left Col: Images */}
                            <div className="flex flex-col gap-4">
                                    <div className="aspect-[2/1] border-[2px] border-[#1a1a1a] bg-[#ccc] overflow-hidden relative">
                                        <Image src={modalData.projectImages.desktop} alt={modalData.altText || `Desktop interface design for ${modalData.name}`} fill className="object-cover object-top" />
                                    </div>
                                    <Link 
                                        href={modalData.blogUrl} 
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#1a1a1a] text-[#e4e2dd] border-[2px] border-[#1a1a1a] font-bold hover:bg-white hover:text-[#1a1a1a] transition-all"
                                    >
                                        VIEW CASE STUDY
                                    </Link>
                            </div>
                            
                            {/* Right Col: Info */}
                            <div className="flex flex-col justify-center">
                                <h2 className="pixel-font text-[2.5rem] mb-2 leading-none">
                                    {modalData.name}
                                </h2>
                                <h3 className="text-[1.2rem] font-bold uppercase mb-4 opacity-70">
                                    {modalData.role}
                                </h3>
                                
                                <div className="mb-6">
                                    <h4 className="font-bold border-b-[2px] border-[#1a1a1a] inline-block mb-2">PROJECT</h4>
                                    <p className="text-lg">{modalData.projectTitle}</p>
                                </div>

                                <div>
                                    <h4 className="font-bold border-b-[2px] border-[#1a1a1a] inline-block mb-2">TESTIMONIAL</h4>
                                    <p className="text-[1.1rem] italic leading-relaxed">
                                        {modalData.testimonial}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
}
